import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  DocumentChartBarIcon,
  DocumentTextIcon,
  BoltIcon,
  PresentationChartLineIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeSolid,
  DocumentChartBarIcon as DocumentChartBarSolid,
  DocumentTextIcon as DocumentTextSolid,
  BoltIcon as BoltSolid,
  PresentationChartLineIcon as PresentationChartLineSolid,
} from '@heroicons/react/24/solid';
import { useAIBar } from '../contexts/AIBarContext';
import { healthTabPath } from './health/HealthViewSwitch';

interface NavigationItem {
  id: string;
  label: string;
  path?: string;
  action?: () => void;
  icon: React.ComponentType<any>;
  solidIcon: React.ComponentType<any>;
  color: string;
}

const BottomNavigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { minimize } = useAIBar();

  const navigationItems: NavigationItem[] = [
    // Five tabs (2026-11-03 redesign): Home, Food, Analysis, Exercise, Health (Supplements | Labs). Labs replaced Coach the
    // same afternoon (Jason: Labs was hard to find, only from the bottom of Home); Coach and Friends are
    // header icons, and the coach is also one tap away in the chat bar and the weekly pattern card.
    { id: 'dashboard', label: 'Home', path: '/dashboard', icon: HomeIcon, solidIcon: HomeSolid, color: '#00E0FF' },
    { id: 'food', label: 'Food', path: '/food-log', icon: DocumentTextIcon, solidIcon: DocumentTextSolid, color: '#00E0FF' },
    { id: 'analysis', label: 'Analysis', path: '/analysis', icon: PresentationChartLineIcon, solidIcon: PresentationChartLineSolid, color: '#00E0FF' },
    { id: 'exercise', label: 'Exercise', path: '/exercise-log', icon: BoltIcon, solidIcon: BoltSolid, color: '#00E0FF' },
    // Health (2026-11-03, Jason: Supplements had no clear way in): Supplements | Labs in one tab,
    // opening whichever view was used last.
    { id: 'health', label: 'Health', action: () => navigate(healthTabPath()), icon: DocumentChartBarIcon, solidIcon: DocumentChartBarSolid, color: '#00E0FF' },
  ];

  const isActive = (item: NavigationItem) => {
    if (item.id === 'health') return location.pathname.startsWith('/supplements') || location.pathname.startsWith('/bloodwork');
    if (!item.path) return false;
    if (item.path === '/dashboard') return location.pathname === '/dashboard' || location.pathname === '/';
    // Recovery (the old Fitness page) lives under the Exercise tab.
    if (item.id === 'exercise') return location.pathname.startsWith('/exercise-log') || location.pathname.startsWith('/fitness');
    return location.pathname.startsWith(item.path);
  };

  const handleNavigation = (item: NavigationItem) => {
    // Always minimize AI chat when navigating
    minimize();
    if (item.action) item.action();
    else if (item.path) navigate(item.path);
  };

  // Instrument-panel tab bar: the active tab is cyan with a glowing bar above its icon.
  return (
    <nav
      className="bottom-navigation"
      data-tour="bottom-nav"
      aria-label="Main"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1100, // Above AI bar
        background: '#0d121a',
        borderTop: '1px solid rgba(0,224,255,0.12)',
        width: '100%',
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${navigationItems.length}, minmax(0, 1fr))`, padding: '6px 4px 10px', minHeight: 68, boxSizing: 'border-box' }}>
        {navigationItems.map((item) => {
          const active = isActive(item);
          const Icon = active ? item.solidIcon : item.icon;
          return (
            <button
              key={item.id}
              onClick={() => handleNavigation(item)}
              aria-label={item.label}
              aria-current={active ? 'page' : undefined}
              className={`bottom-nav-item ${active ? 'active' : ''}`}
              data-tour={item.id === 'food' ? 'food-log' : item.id === 'analysis' ? 'analysis' : undefined}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                minHeight: 52,
                padding: '2px 0',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: active ? item.color : 'rgba(255,255,255,0.6)',
                transition: 'color var(--transition-fast)',
              }}
            >
              <span style={{ width: 18, height: 3, borderRadius: 2, background: active ? item.color : 'transparent', boxShadow: active ? `0 0 8px ${item.color}` : 'none' }} />
              <Icon style={{ width: 22, height: 22, strokeWidth: active ? 2.2 : 1.8 }} />
              <span style={{ fontSize: 11, fontWeight: 600, lineHeight: 1, fontFamily: 'Inter, var(--font-family), sans-serif', whiteSpace: 'nowrap' }}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
      {/* Safe area for iOS devices */}
      <div style={{ height: 'env(safe-area-inset-bottom)', background: '#0d121a' }} />
    </nav>
  );
};

export default BottomNavigation;
